import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { X, Check, Search, ChevronRight, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFileCache } from "@/hooks/useFileCache";

interface FieldMapping {
  sourceColumns: string[];
  targetField: string;
  status: "mapped" | "not-mapped" | "auto-detected";
}

interface MappingLearning {
  sourcePattern: string;
  targetField: string;
  confidence: number;
  lastUsed: string;
  useCount: number;
}

interface FieldMappingDialogProps {
  file: File;
  onComplete: (mappingTemplate: string) => void;
  onCancel: () => void;
}

const TARGET_FIELDS = [
  { field: "patient_id", label: "Patient ID", required: true },
  { field: "date_of_service", label: "Date of Service", required: true },
  { field: "date_of_birth", label: "Date of Birth", required: true },
  { field: "cpt_codes", label: "CPT Codes", required: true, allowMultiple: true },
  { field: "icd10_codes", label: "ICD-10 Codes", required: true, allowMultiple: true },
  { field: "cpt_modifiers", label: "CPT Modifiers", required: false, allowMultiple: true }, // Optional - not every encounter has one
  { field: "place_of_service", label: "Place of Service", required: true },
  { field: "npi", label: "Provider NPI", required: false }, // Optional - uses placeholder if missing
  { field: "claim_id", label: "Claim ID", required: false },
  { field: "first_name", label: "First Name", required: false },
  { field: "last_name", label: "Last Name", required: false },
  { field: "full_patient_name", label: "Full Patient Name", required: false },
  { field: "tin", label: "TIN", required: false },
  { field: "gender", label: "Gender", required: false },
];

export function FieldMappingDialogV3({ file, onComplete, onCancel }: FieldMappingDialogProps) {
  const [mappings, setMappings] = useState<Record<string, FieldMapping>>({});
  const [sourceColumns, setSourceColumns] = useState<string[]>([]);
  const [sampleData, setSampleData] = useState<Record<string, string[]>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [autoMappingSuggestions, setAutoMappingSuggestions] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    totalRows: number;
    validRows: number;
    invalidRows: number;
    warnings: string[];
    errors: Array<{row: number; field: string; message: string}>;
  } | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const { toast } = useToast();
  const { getCachedData, setCachedData } = useFileCache();

  useEffect(() => {
    loadFileHeaders();
  }, [file]);

  const loadFileHeaders = async () => {
    try {
      // Check cache first
      const cached = getCachedData(file);
      if (cached) {
        console.log("[FieldMappingDialogV3] Using cached file data");
        setSourceColumns(cached.headers);
        
        // Extract sample data for display
        const samples: Record<string, string[]> = {};
        cached.headers.forEach((col: string) => {
          samples[col] = [];
          if (cached.preview && cached.preview.length > 0) {
            for (let i = 0; i < Math.min(3, cached.preview.length); i++) {
              if (cached.preview[i][col]) {
                samples[col].push(cached.preview[i][col]);
              }
            }
          }
        });
        setSampleData(samples);
        
        const initialMappings = await autoDetectMappings(cached.headers, samples);
        setMappings(initialMappings);
        setIsLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      
      const token = localStorage.getItem("auth_token");
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/claims/analyze-headers`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to analyze file: ${response.status} ${errorText}`);
      }
      
      const responseData = await response.json();
      
      // Fast path: if response has expected structure, use directly
      const data = responseData.headers ? responseData : (responseData.data || responseData);
      
      // Clean headers
      const cleanedHeaders = data.headers.map((header: string, index: number) => {
        if (!header || header.trim() === '') {
          return `Column ${index + 1}`;
        }
        return header.trim();
      });
      
      setSourceColumns(cleanedHeaders);
      
      // Extract sample data for each column
      const samples: Record<string, string[]> = {};
      cleanedHeaders.forEach((col: string) => {
        samples[col] = [];
        if (data.preview && data.preview.length > 0) {
          // Get up to 3 sample values
          for (let i = 0; i < Math.min(3, data.preview.length); i++) {
            if (data.preview[i][col]) {
              samples[col].push(data.preview[i][col]);
            }
          }
        }
      });
      setSampleData(samples);
      
      // Cache the file data
      setCachedData(file, {
        headers: cleanedHeaders,
        preview: data.preview,
        sampleRow: data.sampleRow,
      });
      
      // Initialize mappings with auto-detection
      const initialMappings = await autoDetectMappings(cleanedHeaders, samples);
      setMappings(initialMappings);
      
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading headers:", error);
      toast({
        title: "Error",
        description: "Failed to analyze file headers",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const loadMappingLearning = async (columns: string[]) => {
    try {
      const token = localStorage.getItem("auth_token");
      console.log("[loadMappingLearning] Auth token:", token ? "Present" : "Missing");
      
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/mapping-learnings/suggestions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ columnNames: columns }),
      });
      
      console.log("[loadMappingLearning] Response status:", response.status);
      
      if (response.ok) {
        const responseData = await response.json();
        const data = responseData.data || responseData;
        return data.suggestions || {};
      } else {
        const errorText = await response.text();
        console.error("[loadMappingLearning] Error response:", response.status, errorText);
      }
    } catch (error) {
      console.error("Error loading learned mappings:", error);
    }
    return {};
  };

  const saveMappingLearning = async (sourceColumn: string, targetField: string, isPositive: boolean, isNew: boolean = false) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      await fetch(`${apiUrl}/api/mapping-learnings/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify({
          sourceColumn,
          targetField,
          isPositive,
          isNew,
        }),
      });
    } catch (error) {
      console.error("Error saving mapping learning:", error);
    }
  };

  const normalizeColumnName = (column: string): string => {
    return column.toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  const autoDetectMappings = async (columns: string[], samples: Record<string, string[]>): Promise<Record<string, FieldMapping>> => {
    const mappings: Record<string, FieldMapping> = {};
    const suggestions: string[] = [];
    
    // Load learned mappings from backend
    console.log('Loading backend suggestions for columns:', columns);
    const learnedSuggestions = await loadMappingLearning(columns);
    console.log('Backend suggestions received:', learnedSuggestions);
    
    columns.forEach(column => {
      const normalized = normalizeColumnName(column);
      let bestMatch: { field: string; confidence: number } | null = null;
      
      // Check learned mappings first
      if (learnedSuggestions[column] && learnedSuggestions[column].length > 0) {
        const topSuggestion = learnedSuggestions[column][0];
        bestMatch = { field: topSuggestion.targetField, confidence: topSuggestion.confidence };
      }
      
      // ONLY use rule-based detection if NO backend learning exists
      // This prevents frontend overrides of backend learning decisions
      if (!bestMatch) {
        TARGET_FIELDS.forEach(target => {
          const targetNorm = normalizeColumnName(target.label);
          let confidence = 0;
          
          // Check for exact matches
          if (normalized === targetNorm) confidence = 1;
          // Check for contains (but exclude MRN from claim_id matching)
          else if ((normalized.includes(targetNorm) || targetNorm.includes(normalized)) &&
                   !(target.field === "claim_id" && (normalized.includes("mrn") || normalized.startsWith("mrn")))) {
            confidence = 0.8;
          }
          // Special cases
          else if (
            (target.field === "patient_id" && (normalized.includes("patid") || normalized.includes("patientid"))) ||
            (target.field === "date_of_service" && (normalized.includes("dos") || normalized.includes("servicedate"))) ||
            (target.field === "date_of_birth" && (normalized.includes("dob") || normalized.includes("birthdate"))) ||
            (target.field === "cpt_codes" && (normalized.includes("cpt") || normalized.includes("procedure"))) ||
            (target.field === "icd10_codes" && (normalized.includes("icd") || normalized.includes("diagnosis"))) ||
            (target.field === "place_of_service" && (normalized.includes("pos") || normalized.includes("location")))
            // Removed MRN -> claim_id mapping to prevent auto-mapping MRN to Claim ID
          ) {
            confidence = 0.9;
          }
          
          if (confidence > 0 && (!bestMatch || confidence > bestMatch.confidence)) {
            bestMatch = { field: target.field, confidence };
          }
        });
      }
      
      if (bestMatch && bestMatch.confidence >= 0.7) {
        // Check if this target field allows multiple columns (many-to-one mapping)
        const targetDef = TARGET_FIELDS.find(t => t.field === bestMatch.field);
        const isManyToOneField = targetDef?.field === 'icd10_codes' || 
                                targetDef?.field === 'cpt_codes' || 
                                targetDef?.field === 'cpt_modifiers';
        
        if (isManyToOneField) {
          // Allow multiple columns for ICD-10, CPT, and CPT modifiers
          mappings[column] = {
            sourceColumns: [column],
            targetField: bestMatch.field,
            status: "auto-detected",
          };
          suggestions.push(`${column} → ${TARGET_FIELDS.find(t => t.field === bestMatch!.field)?.label}`);
        } else {
          // For one-to-one fields, check if target already has a mapping
          const existingMapping = Object.values(mappings).find(m => m.targetField === bestMatch!.field);
          if (!existingMapping || (existingMapping.status !== "auto-detected")) {
            mappings[column] = {
              sourceColumns: [column],
              targetField: bestMatch.field,
              status: "auto-detected",
            };
            suggestions.push(`${column} → ${TARGET_FIELDS.find(t => t.field === bestMatch!.field)?.label}`);
          } else {
            // Target already mapped, mark this column as unmapped
            mappings[column] = {
              sourceColumns: [],
              targetField: "",
              status: "not-mapped",
            };
          }
        }
      } else {
        mappings[column] = {
          sourceColumns: [],
          targetField: "",
          status: "not-mapped",
        };
      }
    });
    
    setAutoMappingSuggestions(suggestions);
    return mappings;
  };

  const handleMappingChange = (sourceColumn: string, targetField: string) => {
    const newMappings = { ...mappings };
    
    if (targetField === "none" || targetField === "") {
      // Learn that this mapping should not be suggested
      if (newMappings[sourceColumn].targetField) {
        saveMappingLearning(sourceColumn, newMappings[sourceColumn].targetField, false);
      }
      
      newMappings[sourceColumn] = {
        sourceColumns: [],
        targetField: "",
        status: "not-mapped",
      };
    } else {
      // Check if this target field allows multiple columns
      const targetDef = TARGET_FIELDS.find(t => t.field === targetField);
      const allowsMultiple = targetDef?.allowMultiple || false;
      
      if (!allowsMultiple) {
        // Clear any existing mapping to this target
        Object.keys(newMappings).forEach(col => {
          if (newMappings[col].targetField === targetField && col !== sourceColumn) {
            newMappings[col] = {
              sourceColumns: [],
              targetField: "",
              status: "not-mapped",
            };
          }
        });
      }
      
      // Learn this mapping
      saveMappingLearning(sourceColumn, targetField, true, true);
      
      newMappings[sourceColumn] = {
        sourceColumns: [sourceColumn],
        targetField: targetField,
        status: "mapped",
      };
    }
    
    setMappings(newMappings);
  };

  const acceptSuggestion = (suggestion: string) => {
    const [source, target] = suggestion.split(" → ");
    const targetField = TARGET_FIELDS.find(t => t.label === target)?.field;
    if (targetField) {
      handleMappingChange(source, targetField);
      // Remove from suggestions after accepting
      setAutoMappingSuggestions(prev => prev.filter(s => s !== suggestion));
    }
  };

  const rejectSuggestion = (suggestion: string) => {
    const [source, target] = suggestion.split(" → ");
    const targetField = TARGET_FIELDS.find(t => t.label === target)?.field;
    if (targetField) {
      // Learn negative feedback
      saveMappingLearning(source, targetField, false);
      // Clear the mapping
      handleMappingChange(source, "none");
    }
    // Remove from suggestions
    setAutoMappingSuggestions(prev => prev.filter(s => s !== suggestion));
  };

  const acceptAllSuggestions = () => {
    const count = autoMappingSuggestions.length;
    autoMappingSuggestions.forEach(suggestion => {
      const [source, target] = suggestion.split(" → ");
      const targetField = TARGET_FIELDS.find(t => t.label === target)?.field;
      if (targetField) {
        handleMappingChange(source, targetField);
        // Save positive feedback for learning
        saveMappingLearning(source, targetField, true);
      }
    });
    setAutoMappingSuggestions([]);
    
    toast({
      title: "All Suggestions Applied",
      description: `Applied ${count} mapping suggestions`,
    });
  };

  const rejectAllSuggestions = () => {
    const count = autoMappingSuggestions.length;
    autoMappingSuggestions.forEach(suggestion => {
      const [source, target] = suggestion.split(" → ");
      const targetField = TARGET_FIELDS.find(t => t.label === target)?.field;
      if (targetField) {
        // Save negative feedback for learning
        saveMappingLearning(source, targetField, false);
      }
    });
    setAutoMappingSuggestions([]);
    
    toast({
      title: "Suggestions Cleared",
      description: `Rejected ${count} suggestions`,
      variant: "default",
    });
  };

  const validateMappings = (): boolean => {
    const requiredFields = TARGET_FIELDS.filter(f => f.required);
    const mappedTargets = new Set(Object.values(mappings).map(m => m.targetField).filter(f => f));
    
    const unmapped = requiredFields.filter(f => !mappedTargets.has(f.field));
    
    if (unmapped.length > 0) {
      toast({
        title: "Missing Required Fields",
        description: `Please map: ${unmapped.map(f => f.label).join(", ")}`,
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };

  const runValidation = async () => {
    if (!validateMappings()) return;
    
    setIsValidating(true);
    try {
      // Format mappings for backend
      const mappingResult: Record<string, string | string[]> = {};
      const groupedMappings: Record<string, string[]> = {};
      
      Object.entries(mappings).forEach(([source, mapping]) => {
        if (mapping.targetField) {
          if (!groupedMappings[mapping.targetField]) {
            groupedMappings[mapping.targetField] = [];
          }
          groupedMappings[mapping.targetField].push(source);
        }
      });
      
      Object.entries(groupedMappings).forEach(([target, sources]) => {
        const targetDef = TARGET_FIELDS.find(t => t.field === target);
        if (targetDef?.allowMultiple) {
          mappingResult[target] = sources;
        } else {
          mappingResult[target] = sources[0];
        }
      });
      
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mappingTemplate", JSON.stringify(mappingResult));
      
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/claims/validate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: formData,
      });
      
      if (response.ok) {
        const responseData = await response.json();
        const result = responseData.data || responseData;
        setValidationResult(result);
        
        // Show summary toast
        if (result.errors && result.errors.length > 0) {
          toast({
            title: "Validation Issues Found",
            description: `${result.errors.length} errors, ${result.warnings?.length || 0} warnings`,
            variant: "destructive",
          });
        } else if (result.warnings && result.warnings.length > 0) {
          toast({
            title: "Validation Warnings",
            description: `File is valid with ${result.warnings.length} warnings`,
            variant: "default",
          });
        } else {
          toast({
            title: "Validation Successful",
            description: `All ${result.totalRows} rows are valid`,
          });
        }
      }
    } catch (error) {
      console.error("Validation error:", error);
      toast({
        title: "Validation Failed",
        description: "Could not validate the file. You may proceed without validation.",
        variant: "destructive",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleComplete = () => {
    if (!validateMappings()) return;
    
    // Convert mappings to format expected by backend
    const mappingResult: Record<string, string | string[]> = {};
    
    // Group by target field for many-to-one mapping
    const groupedMappings: Record<string, string[]> = {};
    Object.entries(mappings).forEach(([source, mapping]) => {
      if (mapping.targetField) {
        if (!groupedMappings[mapping.targetField]) {
          groupedMappings[mapping.targetField] = [];
        }
        groupedMappings[mapping.targetField].push(source);
      }
    });
    
    // Format for backend
    Object.entries(groupedMappings).forEach(([target, sources]) => {
      const targetDef = TARGET_FIELDS.find(t => t.field === target);
      if (targetDef?.allowMultiple) {
        mappingResult[target] = sources;
      } else {
        mappingResult[target] = sources[0];
      }
    });
    
    // Pass both the mapping and the sample row data for TIN checking
    const cached = getCachedData(file);
    onComplete(JSON.stringify({
      mapping: mappingResult,
      sampleRow: cached?.sampleRow || {}
    }));
  };

  const filteredColumns = sourceColumns.filter(col => {
    const matchesSearch = !searchQuery || col.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesUnmapped = !showUnmappedOnly || mappings[col]?.status === "not-mapped";
    return matchesSearch && matchesUnmapped;
  });

  const totalColumns = sourceColumns.length;
  const mappedCount = Object.values(mappings).filter(m => m.status !== "not-mapped").length;

  if (isLoading) {
    return (
      <Dialog open={true} onOpenChange={onCancel}>
        <DialogContent className="max-w-6xl">
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Analyzing file structure...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between gap-4 pr-10">
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Upload Claims File</h3>
              <p className="text-sm text-muted-foreground mt-1">Map your file columns to the appropriate fields</p>
            </div>
            
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8"
              />
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <Switch
                checked={showUnmappedOnly}
                onCheckedChange={setShowUnmappedOnly}
                className="h-5 w-9"
              />
              <label className="text-sm whitespace-nowrap">Show unmapped only</label>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="px-6 py-4 space-y-4 flex-shrink-0">

          {/* Auto-mapping suggestions */}
          {autoMappingSuggestions.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">
                  Auto-mapping suggestions found ({autoMappingSuggestions.length}):
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    onClick={acceptAllSuggestions}
                    className="h-7"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Accept All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={rejectAllSuggestions}
                    className="h-7"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Reject All
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {autoMappingSuggestions.map(suggestion => (
                  <div key={suggestion} className="flex items-center gap-1 bg-white rounded-md px-2 py-1 text-sm">
                    <span>{suggestion}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-5 w-5 p-0 hover:bg-green-100"
                      onClick={() => acceptSuggestion(suggestion)}
                    >
                      <Check className="h-3 w-3 text-green-600" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-5 w-5 p-0 hover:bg-red-100"
                      onClick={() => rejectSuggestion(suggestion)}
                    >
                      <X className="h-3 w-3 text-red-600" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          </div>
          
          {/* Column mapping table */}
          <div className="flex-1 px-6 border-t overflow-hidden">
            <div className="border rounded-lg mt-4 h-full flex flex-col">
              <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b font-medium text-sm flex-shrink-0">
                <div className="col-span-3">Column Name</div>
                <div className="col-span-4">Sample Data</div>
                <div className="col-span-3">Map To</div>
                <div className="col-span-2">Status</div>
              </div>
              
              <ScrollArea className="flex-1">
              {filteredColumns.map(column => {
                const mapping = mappings[column];
                const samples = sampleData[column] || [];
                
                return (
                  <div key={column} className="grid grid-cols-12 gap-4 px-4 py-3 border-b hover:bg-gray-50">
                    <div className="col-span-3 font-medium text-sm flex items-center">
                      {column}
                      {mapping?.status === "auto-detected" && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          Auto-detected
                        </Badge>
                      )}
                    </div>
                    
                    <div className="col-span-4 text-sm text-muted-foreground">
                      {samples.join(" • ")}
                    </div>
                    
                    <div className="col-span-3">
                      <Select
                        value={mapping?.targetField || "none"}
                        onValueChange={(value) => handleMappingChange(column, value)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {TARGET_FIELDS.filter(field => {
                            // Always show multi-column fields (CPT, ICD-10, Modifiers)
                            if (field.allowMultiple) return true;
                            
                            // For single-column fields, only show if not already mapped by another column
                            const isAlreadyMapped = Object.entries(mappings).some(([col, mapping]) => 
                              col !== column && mapping.targetField === field.field
                            );
                            return !isAlreadyMapped;
                          }).map(field => (
                            <SelectItem key={field.field} value={field.field}>
                              {field.label}
                              {field.required && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="col-span-2 flex items-center">
                      <Badge
                        variant={mapping?.status === "mapped" || mapping?.status === "auto-detected" ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {mapping?.status === "mapped" || mapping?.status === "auto-detected" ? "Mapped" : "Not mapped"}
                      </Badge>
                    </div>
                  </div>
                );
              })}
              </ScrollArea>
            </div>
          </div>

          {/* Validation Results Display */}
          {validationResult && (
            <div className="px-6 py-4 border-t flex-shrink-0 overflow-y-auto max-h-[25%]">
              <div className="space-y-3">
                {/* Summary Stats */}
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Validation Results</h4>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600">
                      ✓ {validationResult.validRows} valid
                    </span>
                    {validationResult.invalidRows > 0 && (
                      <span className="text-red-600">
                        ✗ {validationResult.invalidRows} invalid
                      </span>
                    )}
                    {validationResult.warnings && validationResult.warnings.length > 0 && (
                      <span className="text-yellow-600">
                        ⚠ {validationResult.warnings.length} warnings
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Errors Display */}
                {validationResult.errors && validationResult.errors.length > 0 && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="font-medium mb-1">
                        Critical Issues (showing first 5):
                      </div>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        {validationResult.errors.slice(0, 5).map((error, idx) => (
                          <li key={idx}>
                            Row {error.row}: {error.field} - {error.message}
                          </li>
                        ))}
                      </ul>
                      {validationResult.errors.length > 5 && (
                        <p className="text-sm mt-1 text-muted-foreground">
                          ...and {validationResult.errors.length - 5} more errors
                        </p>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Warnings Display */}
                {validationResult.warnings && validationResult.warnings.length > 0 && validationResult.errors?.length === 0 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="font-medium mb-1">Warnings:</div>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        {validationResult.warnings.slice(0, 3).map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 border-t flex items-center justify-between flex-shrink-0">
            <p className="text-sm text-muted-foreground">
              {mappedCount} of {totalColumns} columns mapped
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              {!validationResult && (
                <Button 
                  variant="outline" 
                  onClick={runValidation}
                  disabled={isValidating || mappedCount === 0}
                >
                  {isValidating ? "Validating..." : "Validate"}
                </Button>
              )}
              <Button 
                onClick={handleComplete}
                disabled={validationResult && validationResult.errors && validationResult.errors.length > 0}
              >
                {validationResult ? "Upload" : "Next"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
